import { Controller } from "@hotwired/stimulus"

/**
 * Manage::WhenSlots — Replica SettingsWhenSlot.tsx + SettingsWhenSlotTime.tsx
 *
 * Maneja el grid de slots por dia de la semana:
 * - Toggle activar/desactivar dia (checkbox)
 * - Agregar slot vacio a un dia
 * - Eliminar slot (con confirm si tiene RSVPs)
 * - Editar horas from/to con mascara ##:##
 * - Validacion: al menos un slot debe estar configurado
 * - Genera inputs hidden con los datos finales para el form parent
 */
export default class extends Controller {
  static targets = ["dayRow", "hiddenInputs", "errorMessage"]

  static values = {
    formPrefix: { type: String, default: "manage_bookings_config_form" },
    minSlots: { type: Number, default: 1 },
    errorAtLeastOne: String,
    cantDeleteTitle: String,
    cantDeleteDescription: String,
    noClasses: String,
  }

  connect() {
    this.syncHiddenInputs()
  }

  // --- Acciones ---

  toggleDay(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-day]")
    const day = row.dataset.day
    const isActive = row.dataset.active === "true"

    if (isActive) {
      // Desactivando: verificar que no sea el ultimo dia con slots validos
      const validSlotsInDay = this.countValidSlotsInDay(row)
      if (this.totalValidSlots() <= this.minSlotsValue && validSlotsInDay > 0) {
        this.showError(this.errorAtLeastOneValue)
        return
      }
      row.dataset.active = "false"
      this.updateDayVisuals(row)
    } else {
      // Activando
      row.dataset.active = "true"
      const slotsContainer = row.querySelector("[data-slots]")
      const visibleSlots = slotsContainer.querySelectorAll("[data-slot]:not([data-destroyed='true'])")
      if (visibleSlots.length === 0) {
        this.addSlotToDay(row, "12:00", "")
      }
      this.updateDayVisuals(row)
    }

    this.syncHiddenInputs()
    this.clearError()
  }

  addSlot(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-day]")

    // Si el dia no esta activo, activarlo
    if (row.dataset.active !== "true") {
      row.dataset.active = "true"
      this.updateDayVisuals(row)
    }

    this.addSlotToDay(row, "", "")
    this.syncHiddenInputs()
  }

  deleteSlot(event) {
    event.preventDefault()
    const slotEl = event.currentTarget.closest("[data-slot]")
    const allowDelete = slotEl.dataset.allowDelete !== "false"

    if (!allowDelete) {
      alert(`${this.cantDeleteTitleValue}\n\n${this.cantDeleteDescriptionValue}`)
      return
    }

    // Verificar que no es el ultimo slot valido
    const from = slotEl.querySelector("[data-field='from']").value
    const to = slotEl.querySelector("[data-field='to']").value
    if (from && to && this.totalValidSlots() <= this.minSlotsValue) {
      this.showError(this.errorAtLeastOneValue)
      return
    }

    const slotId = slotEl.dataset.slotId
    if (slotId) {
      // Slot existente en DB: marcar como _destroy
      slotEl.dataset.destroyed = "true"
      slotEl.style.display = "none"
    } else {
      // Slot nuevo: remover del DOM
      slotEl.remove()
    }

    // Si no quedan slots visibles en el dia, desactivarlo
    const row = event.currentTarget.closest("[data-day]")
    const visibleSlots = row.querySelectorAll("[data-slot]:not([data-destroyed='true'])")
    if (visibleSlots.length === 0) {
      row.dataset.active = "false"
      this.updateDayVisuals(row)
    }

    this.syncHiddenInputs()
    this.clearError()
  }

  updateSlot(event) {
    this.applyTimeMask(event.target)
    this.syncHiddenInputs()
    this.clearError()
  }

  // --- Mascara de tiempo ##:## ---

  applyTimeMask(input) {
    let value = input.value.replace(/[^\d]/g, "")
    if (value.length > 4) value = value.substring(0, 4)

    if (value.length >= 3) {
      value = value.substring(0, 2) + ":" + value.substring(2)
    }

    input.value = value
  }

  // --- Helpers internos ---

  addSlotToDay(row, fromValue, toValue) {
    const day = row.dataset.day
    const slotsContainer = row.querySelector("[data-slots]")
    const template = this.buildSlotHTML(day, fromValue, toValue, null, true)
    slotsContainer.insertAdjacentHTML("beforeend", template)
  }

  buildSlotHTML(weekday, from, to, slotId, allowDelete) {
    return `
      <div data-slot data-slot-id="${slotId || ""}" data-allow-delete="${allowDelete}" class="flex items-center mb-[5px] last:mb-0">
        <input type="text"
               data-field="from"
               data-action="input->manage--when-slots#updateSlot"
               value="${this.escapeHTML(from)}"
               placeholder="__:__"
               maxlength="5"
               class="flex-1 w-full h-[32px] rounded-[5px] px-[8px] bg-white text-[13px]" />
        <span class="px-[5px] text-[13px]">-</span>
        <input type="text"
               data-field="to"
               data-action="input->manage--when-slots#updateSlot"
               value="${this.escapeHTML(to)}"
               placeholder="__:__"
               maxlength="5"
               class="flex-1 w-full h-[32px] rounded-[5px] px-[8px] bg-white text-[13px]" />
        <span class="px-[5px]">
          <button type="button"
                  data-action="click->manage--when-slots#deleteSlot"
                  class="flex w-[32px] h-[32px] text-[18px] items-center justify-center hover:opacity-60">
            <i class="fa-regular fa-trash-can"></i>
          </button>
        </span>
      </div>
    `
  }

  updateDayVisuals(row) {
    const isActive = row.dataset.active === "true"
    const checkbox = row.querySelector("[data-checkbox]")
    const timesContainer = row.querySelector("[data-times]")
    const slotsContainer = row.querySelector("[data-slots]")
    const noClasses = row.querySelector("[data-no-classes]")

    // Checkbox visual — replica Checkbox.tsx default: cuadrado 18x18, border-radius:3px, borde negro
    if (checkbox) {
      if (isActive) {
        checkbox.innerHTML = '<span class="inline-flex w-[18px] h-[18px] shrink-0 rounded-[3px] border-2 border-black bg-black items-center justify-center"><i class="fa-solid fa-check text-white text-[11px]"></i></span>'
      } else {
        checkbox.innerHTML = '<span class="inline-flex w-[18px] h-[18px] shrink-0 rounded-[3px] border-2 border-black bg-white items-center justify-center"></span>'
      }
    }

    // Mostrar/ocultar slots vs "Sin clases"
    if (slotsContainer) {
      slotsContainer.style.display = isActive ? "" : "none"
    }
    if (noClasses) {
      const visibleSlots = slotsContainer ? slotsContainer.querySelectorAll("[data-slot]:not([data-destroyed='true'])") : []
      noClasses.style.display = (!isActive || visibleSlots.length === 0) ? "" : "none"
    }
  }

  countValidSlotsInDay(row) {
    const slots = row.querySelectorAll("[data-slot]:not([data-destroyed='true'])")
    let count = 0
    slots.forEach(slot => {
      const from = slot.querySelector("[data-field='from']").value.trim()
      const to = slot.querySelector("[data-field='to']").value.trim()
      if (from && to) count++
    })
    return count
  }

  totalValidSlots() {
    let total = 0
    this.dayRowTargets.forEach(row => {
      if (row.dataset.active === "true") {
        total += this.countValidSlotsInDay(row)
      }
    })
    return total
  }

  // --- Sync inputs hidden para el form parent ---

  syncHiddenInputs() {
    const container = this.hiddenInputsTarget
    container.innerHTML = ""

    let index = 0
    this.dayRowTargets.forEach(row => {
      const day = row.dataset.day
      const isActive = row.dataset.active === "true"
      const slots = row.querySelectorAll("[data-slot]")

      slots.forEach(slotEl => {
        const slotId = slotEl.dataset.slotId
        const isDestroyed = slotEl.dataset.destroyed === "true"
        const from = slotEl.querySelector("[data-field='from']")?.value || ""
        const to = slotEl.querySelector("[data-field='to']")?.value || ""

        const prefix = `${this.formPrefixValue}[slots_attributes][${index}]`

        if (slotId) {
          container.insertAdjacentHTML("beforeend",
            `<input type="hidden" name="${prefix}[id]" value="${slotId}" />`)
        }
        container.insertAdjacentHTML("beforeend",
          `<input type="hidden" name="${prefix}[weekday]" value="${day}" />`)
        container.insertAdjacentHTML("beforeend",
          `<input type="hidden" name="${prefix}[from]" value="${this.escapeHTML(from)}" />`)
        container.insertAdjacentHTML("beforeend",
          `<input type="hidden" name="${prefix}[to]" value="${this.escapeHTML(to)}" />`)

        if (!isActive || isDestroyed) {
          container.insertAdjacentHTML("beforeend",
            `<input type="hidden" name="${prefix}[_destroy]" value="true" />`)
        }

        index++
      })
    })
  }

  // --- Errores ---

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.style.display = ""
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.style.display = "none"
    }
  }

  // --- Utilidades ---

  escapeHTML(str) {
    const div = document.createElement("div")
    div.textContent = str || ""
    return div.innerHTML
  }
}
